import moment from 'moment';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import CalendarDay from '@app/state/calendar/CalendarDay';
import CalendarRange from '@app/state/calendar/CalendarRange';
import Utilities from '@util/Utilities';
import Month from '@app/model/market_state/Month';
import YearMonth from '@app/state/calendar/YearMonth';
import OperationSettings from '@app/model/company_hotel/settings/OperationSettings';
import OperationType from '@app/model/company_hotel/settings/OperationType';

class CalendarCalculator {
	public static create = (year: number, month: Month): CalendarMonth => {
		const days = CalendarCalculator.calculateDays(year, month);
		// @ts-ignore
		return new CalendarMonth({
			index: { year, month },
			display: moment(new Date(year, Utilities.getMonthOrdinal(month), 1)).format('MMMM yyyy'),
			range: CalendarCalculator.getRange(days),
			days: days,
		});
	};

	public static constructFromDate = (date: Date) => {
		return { year: date.getFullYear(), month: Utilities.getMonthByIndex(date.getMonth()) };
	};

	public static now = (operation: OperationSettings) => {
		const now = new Date();

		switch (operation.type) {
			case OperationType.ALL_YEAR:
				return CalendarCalculator.constructFromDate(now);
			case OperationType.SEASONAL:
				if (!operation.seasonFrom || !operation.seasonTo) {
					return CalendarCalculator.constructFromDate(now);
				}
				if (now.getMonth() >= Utilities.getMonthOrdinal(operation.seasonFrom) && now.getMonth() <= Utilities.getMonthOrdinal(operation.seasonTo)) {
					return CalendarCalculator.constructFromDate(now);
				}
				const year = (now.getMonth() > Utilities.getMonthOrdinal(operation.seasonTo)) ? now.getFullYear() + 1 : now.getFullYear();
				return { year: year, month: operation.seasonFrom };
		}
	};

	public static next = (current: YearMonth) => {
		const date = new Date(current.year, Utilities.getMonthOrdinal(current.month), 1);
		const next = moment(date).add(1, 'month');
		return { year: next.year(), month: Utilities.getMonthByIndex(next.month()) };
	};

	public static previous = (current: YearMonth) => {
		const date = new Date(current.year, Utilities.getMonthOrdinal(current.month), 1);
		const previous = moment(date).subtract(1, 'month');
		return { year: previous.year(), month: Utilities.getMonthByIndex(previous.month()) };
	};

	public static set = (yearMonth: YearMonth) => {
		return { year: yearMonth.year, month: yearMonth.month };
	};

	private static getEntry = (date: Date, isPreviousMonth: boolean, isNextMonth: boolean): CalendarDay => {
		const m = moment(date);
		return {
			index: { year: m.year(), month: Utilities.getMonthByIndex(m.month()), day: m.date() },
			date: date,
			week: m.week(),
			weekDay: m.format('iii'),
			isNextMonth: isNextMonth,
			isPreviousMonth: isPreviousMonth,
			isCurrentMonth: !isPreviousMonth && !isNextMonth,
			isMonday: m.weekday() == 0,
			isPast: m.isBefore() && !m.isSame(new Date(), 'day'),
			isToday: m.isSame(new Date(), 'day'),
			display: m.format('yyyy-MM-DD'),
			is: (utcDate: string) => m.format('yyyy-MM-DD') == utcDate,
		};
	};

	private static isNextMonth = (month: Month, year: number, d: Date) => {
		return d.getMonth() > Utilities.getMonthOrdinal(month) || d.getFullYear() > year;
	};

	private static isPreviousMonth = (month: Month, year: number, d: Date) => {
		return d.getMonth() < Utilities.getMonthOrdinal(month) || d.getFullYear() < year;
	};

	private static calculateDays = (year: number, month: Month) => {
		const entries: CalendarDay[] = [];
		if (!!month && year >= 0) {
			const startOfMonth: Date = new Date(year, Utilities.getMonthOrdinal(month), 1);
			let d = new Date(year, Utilities.getMonthOrdinal(month), 1);
			const startOfMonthIsSaturday = startOfMonth.getDay() == 6 && moment(startOfMonth).daysInMonth() > 30;
			const startOfMonthIsSunday = startOfMonth.getDay() == 0;

			let startMonth = false;

			for (let week = 0; week < ((startOfMonthIsSaturday || startOfMonthIsSunday) ? 6 : 5); week++) {
				for (let weekDay = 1; weekDay < 8; weekDay++) {
					//first week check for previous month entries
					if (week === 0) {
						const startDay = startOfMonthIsSunday ? 7 : startOfMonth.getDay();
						if (startDay > weekDay) {
							//previous month day
							const prevMonthDay = moment(d).add(weekDay - startDay, 'days').toDate();
							entries.push(CalendarCalculator.getEntry(prevMonthDay, true, false));
						} else startMonth = true;
					}

					if (startMonth) {
						entries.push(CalendarCalculator.getEntry(d, false, CalendarCalculator.isNextMonth(month, year, d)));
						d = moment(d).add(1, 'days').toDate();
					}
				}
			}
		}
		return entries;
	};

	public static getRange = (entries: CalendarDay[]): CalendarRange => {
		return { startStatDate: entries[0].display, endStatDate: entries[entries.length - 1].display };
	};

	public static nextCalendarMonthDay(date: string, calendarMonth: CalendarMonth) {
		const index = calendarMonth.days.findIndex(d => d.display == date);
		if (index < calendarMonth.days.length - 1) {
			return calendarMonth.days[index + 1];
		} else return undefined;
	}

	public static nextCalendarMonthWeek(date: string, calendarMonth: CalendarMonth) {
		const index = calendarMonth.days.findIndex(d => d.display == date);
		if ((index + 7) < calendarMonth.days.length - 1) {
			return calendarMonth.days[index + 7];
		} else return undefined;
	}

	static previousCalendarMonthDay(date: string, calendarMonth: CalendarMonth) {
		const index = calendarMonth.days.findIndex(d => d.display === date);
		if (index > 0) {
			return calendarMonth.days[index - 1];
		} else return undefined;
	}
	public static previousCalendarMonthWeek(date: string, calendarMonth: CalendarMonth) {
		const index = calendarMonth.days.findIndex(d => d.display == date);
		if ((index - 7) > 0) {
			return calendarMonth.days[index - 7];
		} else return undefined;
	}
	static todayCalendarMonthDay(calendarMonth: CalendarMonth) {
		const index = calendarMonth.days.findIndex(d => d.isToday);
		if (index > -1) {
			return calendarMonth.days[index];
		} else return undefined;
	}
	static firstCalendarMonthDay(calendarMonth: CalendarMonth) {
		return calendarMonth.days[0];
	}
}

export default CalendarCalculator;